import { Button, TextArea, TextInput } from '@cimplico/components';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CategorySelect from '../../components/procedure-category-select/ProcedureCategorySelect';
import EntityTypeSelect from '../../components/entity-type-select/EntityTypeSelect';
import RoleSelect from '../../components/role-select/RoleSelect';
import { toastNotification } from '../../components/toast/Toast';
import { useFirmContext } from '../../providers/FirmProvider';
import { useCreateProcedures } from '../../queries/use-create-procedures';
import { EntityTypeEnum } from '../../types/entity';

const NAME_MAX_CHAR = 250;
const DESCRIPTION_MAX_CHAR = 2000;

function NewTemplateProcedureForm(): JSX.Element {
    const navigate = useNavigate();
    const { shortId } = useFirmContext();
    const { id: templateId } = useParams();

    const [name, setName] = useState<{ value?: string; errorMessage?: string }>();
    const [description, setDescription] = useState<{ value?: string; errorMessage?: string }>();
    const [categoryId, setCategoryId] = useState<string | undefined>();
    const [entityTypes, setEntityTypes] = useState<EntityTypeEnum[]>();
    const [role, setRole] = useState<{ value?: string; errorMessage?: string }>();

    function handleCancel(): void {
        navigate(`/${shortId}/templates/procedures/${templateId}`);
    }

    const createTemplateProcedureQuery = useCreateProcedures();

    function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
        e.preventDefault();

        if (!name || !name.value) {
            setName({ errorMessage: 'A procedure name must be provided.' });
            return;
        }

        if (!description || !description.value) {
            setDescription({ errorMessage: 'A description must be provided.' });
            return;
        }

        if (!role || !role.value) {
            setRole({ errorMessage: 'A role must be selected.' });
            return;
        }

        createTemplateProcedureQuery.mutate(
            {
                templateId: templateId as string,
                procedures: [
                    {
                        name: name.value,
                        description: description.value,
                        role: role.value,
                        entityTypes: entityTypes || [],
                        categoryId,
                    },
                ],
            },
            {
                onSuccess: () => {
                    navigate(`/${shortId}/templates/procedures/${templateId}`);
                    toastNotification('success', 'New template procedure created!');
                },
                onError: (err) => {
                    const result = err.response?.data.results[0];

                    if (result.error?.fields?.name) {
                        setName((prev) => ({ ...prev, errorMessage: result.error.fields.name }));
                    }

                    if (result.error?.fields?.description) {
                        setDescription((prev) => ({ ...prev, errorMessage: result.error.fields.description }));
                    }

                    toastNotification('error', 'Unable to create new template procedure!');
                },
            }
        );
    }

    return (
        <form className="flex flex-col gap-8" onSubmit={handleSubmit}>
            {/* PROCEDURE NAME */}
            <TextInput
                placeholder="e.g. Clear suspense account and all queries"
                title="Procedure name"
                onChange={(value): void => {
                    setName({ value, errorMessage: '' });
                }}
                errorMessage={name?.errorMessage}
                characterLimit={NAME_MAX_CHAR}
                autoFocus
            />

            {/* PROCEDURE DESCRIPTION */}
            <TextArea
                title="Add a description"
                onChange={(value): void => {
                    setDescription({ value, errorMessage: '' });
                }}
                errorMessage={description?.errorMessage}
                placeholder="Description..."
                form="new-procedure-form"
                characterLimit={DESCRIPTION_MAX_CHAR}
            />

            {/* ROLE TYPE SELECT */}
            <RoleSelect
                title="Role"
                placeholder="Select a role..."
                errorMessage={role?.errorMessage}
                onChange={(value): void => {
                    setRole({ value, errorMessage: '' });
                }}
            />

            {/* CATEGORY SELECT */}
            {templateId && (
                <CategorySelect isOptional title="Category" onChange={(v): void => setCategoryId(v[0])} templateId={templateId} />
            )}

            {/* ENTITY TYPE SELECT */}
            <EntityTypeSelect
                isMulti
                isOptional
                placeholder="Select entity types..."
                title="Entity Type"
                defaultEntityType={entityTypes || []}
                onChange={setEntityTypes}
            />

            <div className="flex justify-end gap-4">
                <Button type="hollow" label="Cancel" onClick={handleCancel} />
                <Button
                    type="primary"
                    label={createTemplateProcedureQuery.isLoading ? 'Creating...' : 'Create Procedure'}
                    disabled={createTemplateProcedureQuery.isLoading}
                    isLoading={createTemplateProcedureQuery.isLoading}
                    isSubmit
                />
            </div>
        </form>
    );
}

export default NewTemplateProcedureForm;
